<template>
	<div class="order-summary-container" :class="{noMiniActions, 'swipe-delete': isSwipingLeft}" v-swipe="swipeHandler">
		<div class="order-head" :class="{'no-pointer': !ordersStore.selectedOrderGroup?.orders?.length || ordersStore.selectedOrderGroup?.orders?.length > 1}" @click="clickOrderHead">
			<div class="first-row" v-if="ordersStore.selectedOrderGroup">
				<div class="title-container">
					<div class="title-icon" v-if="ordersCustomer" v-html="getIcon('iconUser')" @click.stop="openUserDetails = ordersCustomer"></div>
					<div class="title" :style="'color: '+ordersStore.selectedOrderGroup.statusColor">{{ ordersStore.selectedOrderGroup.title }}</div>
				</div>
				<div class="date">{{ orderDate ? orderDate : '' }}</div>
			</div>
			<div class="second-row" v-if="ordersStore.selectedOrderGroup">
				<div class="state-container"  :style="'--status-color: '+ordersStore.selectedOrderGroup.statusColor+'; --payment-color: '+ordersStore.selectedOrderGroup.paymentColor+'; --paid-color: '+(ordersStore.selectedOrderGroup.paid ? '#62D18D' : '#E24B74')+';'">
					<div class="payment-container" v-if="!orderIsCompleted">
						<div class="payment-icon" :class="{pluginIcon}"><img :src="orderStatusIcon" alt="payment icon"></div>
					</div>
					<div class="order-state" v-if="!orderIsCompleted">
						<div class="status">{{ orderStatus }}</div>
					</div>
					<div class="order-state" v-else>
						<div class="status">{{ ordersStore.selectedOrderGroup.paymentMethodName }}</div>
					</div>
				</div>
				<div class="order-share-container" @click.stop="shareLink">
					<div class="orders-number">{{ ordersNumber }}</div>
					<div class="share-icon" v-if="canShareLink" v-html="getIcon('iconShare')"></div>
				</div>
			</div>
			<div class="first-row" v-if="!ordersStore.selectedOrderGroup">
				<div class="title">{{ getString('no_order_selected') }}</div>
			</div>
			<div class="second-row" v-if="!ordersStore.selectedOrderGroup">
				<div class="ghost-placeholder"></div>
				<div class="ghost-placeholder"></div>
			</div>
		</div>
		<div class="order-items">
			<div class="all-items-together" v-if="ordersStore.selectedOrderGroup && !divideOrders">
				<div class="items-summary-container">
					<OrderSummaryItem v-for="item in allOrdersItems" :key="item.identifier" :item="item" :done="itemIsDone(item)" :with-symbol="!orderIsCompleted" :show-prepared-icon="showPreparedIcon && item.identifier !== 'tip'" />
					<template v-if="allOrdersItems.length === 0">
						<OrderSummaryItemFake v-if="ordersTotals.rawTotal > 0 && selectedCourse === null" :title="getString('amount')" :price="ordersTotals.rawTotal" :quantity="1" />
						<OrderSummaryItem v-else :item="null" />
					</template>
					<template v-for="order in allOrders" v-if="selectedCourse === null">
						<OrderSummaryItemFake :title="coverChargeName(order)" v-if="order.cover_charge_num > 0" :price="order.cover_charge_value" :quantity="order.cover_charge_num" image="https://img.qromo.io/img/icons/icon-coperto-plus-cost.svg" />
						<OrderSummaryItemFake :title="getString('delivery_costs')" v-if="order.delivery_costs_value > 0" :price="order.delivery_costs_value" :quantity="null" image="https://img.qromo.io/img/icons/icon-delivery-plus-cost.svg" />
						<OrderSummaryItemFake :title="getString('takeaway_costs')" v-if="order.takeaway_costs_value > 0" :price="order.takeaway_costs_value" :quantity="null" image="https://img.qromo.io/img/icons/icon-takeaway-plus-cost.svg" />
					</template>
				</div>
				<div class="notes-container" v-if="ordersStore.selectedOrderGroup.orders.length === 1 && !!ordersStore.selectedOrderGroup.orders[0].notes">
					<div class="notes-title">{{ getString('order_notes') }}</div>
					<div class="notes-body">{{ ordersStore.selectedOrderGroup.orders[0].notes }}</div>
				</div>
			</div>
			<div class="items-divided" v-if="ordersStore.selectedOrderGroup && divideOrders">
				<AppButton class="button-divide" :text="getString('unite_orders')" @click="performUniteOrder" outline small :disabled="!userStore.checkOrderEditAllowed" :loading="loadingAction" />
				<div class="items-divided-order" v-for="(order, orderIndex) in ordersStore.selectedOrderGroup.orders" :key="order.order_id">
					<div class="items-order-header" @click="clickSubOrderHead(orderIndex)">
						<div class="items-order-number">{{ order.orderNumberDayFormatted }}</div>
						<div class="items-order-date">{{ getOrderFormattedDate(order) }}</div>
					</div>
					<div class="items-summary-container">
						<OrderSummaryItem v-for="item in order.items" :key="item.order_item_id" :dark-background="ordersStore.selectedOrderGroup && divideOrders" :item="item" :done="!orderIsCompleted && (ordersStore.selectedOrderGroup.status === ordersStore.STATUS.prepared || item.course < order.course)" :with-symbol="!orderIsCompleted" :show-prepared-icon="showPreparedIcon" />
						<OrderSummaryItemFake v-if="order.items.length === 0 && order.subtotal_delta > 0" :title="getString('amount')" :price="order.subtotal_delta" :quantity="1" />
						<OrderSummaryItemFake :title="getString('cover_charge')" v-if="order.cover_charge_num > 0" :price="order.cover_charge_value" :quantity="order.cover_charge_num" image="https://img.qromo.io/img/icons/icon-coperto-plus-cost.svg" />
						<OrderSummaryItemFake :title="getString('delivery_costs')" v-if="order.deliveryCosts > 0" :price="order.deliveryCosts" :quantity="null" image="https://img.qromo.io/img/icons/icon-delivery-plus-cost.svg" />
						<OrderSummaryItemFake :title="getString('takeaway_costs')" v-if="order.takeawayCosts > 0" :price="order.takeawayCosts" :quantity="null" image="https://img.qromo.io/img/icons/icon-takeaway-plus-cost.svg" />
					</div>
					<div class="notes-container" v-if="!!order.notes">
						<div class="notes-title">{{ getString('order_notes') }}</div>
						<div class="notes-body">{{ order.notes }}</div>
					</div>
					<div class="single-order-actions">
						<AppButton v-if="!orderIsCompleted && singleOrderHasAction(order)" :disabled="ordersAction.actionId === 'payment' && !userStore.checkAnyOrderPayAllowed"
								   class="button-divided-action" :text="singleOrderActionText(order)"
								   @click="orderActionClicked(order)" outline small :loading="loadingAction" />
						<AppButtonIcon v-if="isSingleOrderEditable(order)" small class="mini-action" icon-name="icon-edit-white"
									   :disabled="!userStore.checkOrderEditAllowed" prevent-change @click="editOrder(order)" />
					</div>
				</div>
			</div>
			<div class="no-items" v-if="!ordersStore.selectedOrderGroup">
				<OrderSummaryItem />
			</div>
		</div>
		<div class="courses-selector-container" v-if="showCourses">
			<div class="courses-selector">
				<div class="course-selector" v-for="i in maxCourse" :key="i" :class="{selected: selectedCourse === i, current: !orderIsCompleted && runningCourse === i, done: !orderIsCompleted && i < runningCourse}" @click="clickCourse(i)">{{ i }}</div>
			</div>
		</div>
		<div class="order-bottom">
			<div class="order-totals" v-if="ordersStore.selectedOrderGroup">
				<div class="total-row">
					<div class="total-row-title">{{ getString("total") }}</div>
					<div class="total-row-value total-bold">
						<img v-if="ordersMultiHasPayments" @click="multiPaymentsActive = true" :src="'https://img.qromo.io/img/icons/icon-multipay-blue-new'+(userStore.darkMode ? '-dark' : '')+'.svg'">
						<span class="total-value-discount" v-if="ordersTotals.rawTotal !== ordersTotals.rawTotalWithoutDiscount">{{ ordersTotals.totalWithoutDiscount }}</span>
						{{ ordersTotals.total }}
					</div>
				</div>
			</div>
			<div class="order-totals" v-if="!ordersStore.selectedOrderGroup">
				<div class="total-row">
					<div class="total-row-title ghost-placeholder"></div>
					<div class="total-row-value ghost-placeholder short"></div>
				</div>
				<div class="total-row mt-8 mb-4">
					<div class="total-row-title ghost-placeholder"></div>
					<div class="total-row-value ghost-placeholder short"></div>
				</div>
				<div class="total-row-separator"></div>
				<div class="total-row mt-4 mb-4">
					<div class="total-row-title ghost-placeholder"></div>
					<div class="total-row-value ghost-placeholder short"></div>
				</div>
				<div class="total-row-separator"></div>
			</div>
			<div class="order-actions" v-if="ordersStore.selectedOrderGroup">
				<div class="row-mini-actions">
					<div v-if="showEditButton" class="custom-icon-button mini-action" :class="{disabled: !userStore.checkOrderEditAllowed}" @click="editOrders">
						<div class="btn-inner">
							<img :src="'https://img.qromo.io/img/icons/icon-edit-white'+(userStore.darkMode ? '-dark' : '-grey')+'.svg'" alt="edit" />
						</div>
					</div>
					<div v-if="showDeliveryInfoButton" class="custom-icon-button mini-action" @click="showDeliveryInfo">
						<div class="btn-inner">
							<img :src="'https://img.qromo.io/img/icons/info-white'+(userStore.darkMode ? '-dark' : '-grey')+'.svg'" alt="info" />
						</div>
					</div>
					<div v-if="showEmailButton" class="custom-icon-button mini-action" @click="activeMessageSheet = true">
						<div class="btn-inner">
							<img :src="'https://img.qromo.io/img/icons/icon-email-order'+(userStore.darkMode ? '-dark' : '-grey')+'.svg'" alt="email" />
						</div>
					</div>
					<div v-if="showPrintersButton" class="custom-icon-button mini-action" :class="{disabled: !userStore.checkOrderCreationAllowed && !userStore.checkAnyOrderPayAllowed, loading: loadingActionPrinter}" @click="printOrderAgain">
						<div class="btn-inner">
							<AppLoader v-if="loadingActionPrinter" :size="13" :color="userStore.darkMode ? '#fff' : '#0752A3'" />
							<img v-else :src="'https://img.qromo.io/img/icons/icon-print-order'+(userStore.darkMode ? '-dark' : '-grey')+'.svg'" alt="print" />
						</div>
					</div>
					<div v-if="showRefundButton" class="custom-icon-button mini-action" :class="{loading: loadingRefundTransaction}" @click="startOrderRefund">
						<div class="btn-inner">
							<AppLoader v-if="loadingRefundTransaction" :size="13" :color="userStore.darkMode ? '#fff' : '#0752A3'" />
							<img v-else :src="'https://img.qromo.io/img/icons/icon-refund-new'+(userStore.darkMode ? '-dark' : '')+'.svg'" alt="refund" />
						</div>
					</div>
					<div v-if="showAdeSendButton" class="custom-icon-button mini-action" :class="{disabled: !userStore.checkAnyOrderPayAllowed}" @click="showSendReceipt">
						<div class="btn-inner">
							<img :src="'https://img.qromo.io/img/icons/icon-ade-white'+(userStore.darkMode ? '-dark' : '-grey')+'.svg'" alt="ade" />
						</div>
					</div>
					<div v-if="showAdeEpsonSendButton" class="custom-icon-button mini-action" :class="{disabled: !userStore.checkAnyOrderPayAllowed}" @click="showSendReceiptEpson">
						<div class="btn-inner">
							<img :src="'https://img.qromo.io/img/icons/icon-ade-white'+(userStore.darkMode ? '-dark' : '-grey')+'.svg'" alt="ade epson" />
						</div>
					</div>
					<div v-if="showSmsButton" class="custom-icon-button mini-action" :class="{loading: loadingActionSms}" :style="'--button-color: #8F4DFE'" @click="sendSms">
						<div class="btn-inner">
							<AppLoader v-if="loadingActionSms" :size="13" :color="userStore.darkMode ? '#fff' : '#0752A3'" />
							<img v-else :src="'https://img.qromo.io/img/icons/icon-send-sms'+(userStore.darkMode ? '-dark' : '-grey')+'.svg'" alt="sms" />
						</div>
					</div>
					<div v-if="showAdeButton" class="custom-icon-button mini-action" :class="{disabled: !userStore.checkAnyOrderPayAllowed, loading: loadingActionAde}" :style="'--button-color: #fd831d'" @click="printFiscalReceipt">
						<div class="btn-inner">
							<AppLoader v-if="loadingActionAde" :size="13" :color="userStore.darkMode ? '#fff' : '#0752A3'" />
							<img v-else :src="'https://img.qromo.io/img/icons/icon-ade-white'+(userStore.darkMode ? '-dark' : '-grey')+'.svg'" alt="ade" />
						</div>
					</div>
					<div v-if="showCancelButton" class="custom-icon-button mini-action" :class="{disabled: !userStore.checkOrderEditAllowed, loading: loadingActionRefuse}" :style="'--button-color: var(--color-bg-primary-1)'" @click="isRefuseAction = false; $refs['refuse-confirm'].toggleStatus(true)">
						<div class="btn-inner">
							<AppLoader v-if="loadingActionRefuse" :size="13" color="#0752A3" />
							<img v-else :src="'https://img.qromo.io/img/icons/icon-trash-primary'+(userStore.darkMode ? '-dark' : '')+'.svg'" alt="cancel" />
						</div>
					</div>
					<div v-if="showCancelPartialButton" class="custom-icon-button mini-action" :class="{disabled: !userStore.checkOrderEditAllowed, loading: loadingActionRefuse}" :style="'--button-color: var(--color-bg-primary-1)'" @click="$refs['refuse-partial-confirm'].toggleStatus(true)">
						<div class="btn-inner">
							<AppLoader v-if="loadingActionRefuse" :size="13" color="#0752A3" />
							<img v-else :src="'https://img.qromo.io/img/icons/icon-x-primary'+(userStore.darkMode ? '-dark' : '')+'.svg'" alt="cancel partial" />
						</div>
					</div>
				</div>
				<div class="row-main-action">
					<div v-if="showRefuseButton" class="custom-button refuse outline" :class="{loading: loadingActionRefuse}" @click="isRefuseAction = true; $refs['refuse-confirm'].toggleStatus(true)">
						<AppLoader v-if="loadingActionRefuse" :size="14" />
						<span class="button-text" v-else>{{ getString('refuse') }}</span>
					</div>
					<div v-if="showPaymentSecondaryButton" class="custom-button refuse outline" :class="{disabled: !userStore.checkAnyOrderPayAllowed, loading: loadingAction}" @click="ordersActionClicked('payment')">
						<AppLoader v-if="loadingAction" :size="14" />
						<span class="button-text" v-else>{{ getString('pay') }}</span>
					</div>
					<div class="custom-button accept" :class="{fullWidth: !showRefuseButton && !showPaymentSecondaryButton, disabled: disableMainAction, loading: loadingAction}" @click="ordersActionClicked(null)">
						<AppLoader v-if="loadingAction" :size="14" color="#ffffff" />
						<span class="button-text" v-else>{{ ordersAction.text }}</span>
					</div>
				</div>
			</div>
			<div class="order-actions" v-if="!ordersStore.selectedOrderGroup">
				<div class="row-main-action">
					<div class="ghost-button-placeholder"></div>
				</div>
			</div>
			<AppBottomSheet :forceWidth="bottomSheetWidth" :active="activeMessageSheet" @toggling-popup="toggleBottomSheet" >
				<div class="send-message-content" v-if="activeMessageSheet">
					<ModalHeader
						:title="getString('send_a_message_to_customer')"
						:button-text="getString('send')"
						:button-loading="loadingMessage"
						@click-button="sendOrderMessage" />
					<div class="row space-between mt-20 mb-10">
						<p class="row-title">{{ getString('message') }}</p>
						<AppTextInput class="textarea-input" ref="messageInput" textarea :placeholder="getString('message_placeholder')" />
					</div>
				</div>
			</AppBottomSheet>
			<AppBottomSheet :forceWidth="bottomSheetWidthSmall" :active="activeReprintSheet" @toggling-popup="toggleBottomSheetReprint" >
				<div class="reprint-content" v-if="activeReprintSheet">
					<ModalHeader :title="getString('order_receipts')" />
					<div class="row space-between mt-20 mb-10">
						<p class="row-title">{{ getString('print_order') }}</p>
						<AppButton outline small :text="getString('print')" @click="printAggregateOrder" :loading="loadingActionPrinter"
								   :disabled="!userStore.checkAnyOrderPayAllowed"/>
					</div>
					<div class="row space-between mt-20 mb-10">
						<p class="row-title">{{ getString('print_prebill') }}</p>
						<AppButton outline small :text="getString('print')" @click="printPrebill" :loading="loadingActionPrinterPrebill"
								   :disabled="!userStore.checkOrderCreationAllowed && !userStore.checkAnyOrderPayAllowed"/>
					</div>
					<div class="row space-between mt-20 mb-10" v-if="ordersStore.selectedOrderGroup && ordersStore.selectedOrderGroup.type === 'stripe'">
						<p class="row-title">{{ getString('pos_receipt') }}</p>
						<AppButton outline small :text="getString('receipt')" @click="showReceiptManager = true" :disabled="!userStore.checkAnyOrderPayAllowed"/>
					</div>
				</div>
			</AppBottomSheet>
			<ReceiptManager :active="showReceiptManager" @toggling-popup="toggleReceiptManager" @success="successReceipt" />
			<RefundTransactionPopup
				:transaction="refundingTransaction"
				@refunded="refundSuccess"
				@failed="refundFailed"
				@closed="refundingTransaction = null"
			/>
			<AppPopup ref="prebill-type" :title="getString('prebill_paid_items')" :buttons="[{text: getString('print_only_paid'), callback: () => sendPrintPrebill(false) }, {text: getString('print_all'), callback: () => sendPrintPrebill(true) }]" />
			<OrderPayments :order="ordersStore.selectedOrderGroup" @open-user-details="openPaymentUserDetails" @refund-payment="startPaymentRefund" :loading-refund-payment-id="loadingRefundPaymentId" :active="multiPaymentsActive" @toggling-popup="togglingMultiPayments" :payments="multiPayments" />
			<AppBottomSheet :forceWidth="bottomSheetWidthSmall" :active="!!openUserDetails" @toggling-popup="toggleBottomSheetUserDetails" >
				<CustomersViewer v-if="!!openUserDetails" @customer-not-found="openUserDetails = null" :customer-id="openUserDetails.customer_id" :customer-type="openUserDetails.type" />
			</AppBottomSheet>
			<AppPopup ref="refuse-confirm" :title="getString('refuse_order')" :body-text="getString('refuse_order_text')" :buttons="[{text: getString('cancel'), negative: true}, {text: getString('continue'), callback: refuseOrder}]" />
			<AppPopup ref="refuse-partial-confirm" :title="getString('refuse_order')" :body-text="getString('refuse_partial_order_text')" :buttons="[{text: getString('cancel'), negative: true}, {text: getString('continue'), callback: refusePartialOrder}]" />
			<AppBottomSheet :force-width="bottomSheetWidthLarge" :active="!!showOrderHistory" @toggling-popup="toggleOrderHistory">
				<OrderHistory v-if="!!showOrderHistory && ordersStore?.selectedOrderGroup?.orders?.length" :order="showOrderHistory" />
			</AppBottomSheet>
		</div>
	</div>
</template>

<script>
import {useCurrencyStore, useLanguageStore, useNotificationStore, useSvgStore} from "@/shared/stores";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppLoader from "@/shared/components/AppLoader.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import OrderSummaryItem from "@/console-new/components/ops/orders/OrderSummaryItem.vue";
import {formatDateMMDDHHmm, getMinutesPassedFromDate} from "@/shared/helpers/dates";
import API from "@/shared/helpers/API";
import {
	useAdministrationStore,
	useCounterStore,
	useEpsonStore, useFunnelStore,
	useNativeStore,
	useOrdersActionsStore,
	useOrdersStore,
	usePrintersStore,
	useUserStore
} from "@/console-new/stores";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppPopup from "@/shared/components/AppPopup.vue";
import OrderSummaryItemFake from "@/console-new/components/ops/orders/OrderSummaryItemFake.vue";
import OrderPayments from "@/console-new/components/ops/orders/OrderPayments.vue";
import BottomPayment from "@/console-new/components/ops/counter/BottomPayment.vue";
import CustomersViewer from "@/console-new/components/ops/CustomersViewer.vue";
import {orderFiscalizedStatus, isTransactionRefundable, isInRefundWindow} from "@/console-new/helpers/orders";
import RefundTransactionPopup from "@/console-new/components/shared/RefundTransactionPopup.vue";
import {getNative, isNative} from "@/shared/helpers/webapp";
import AppNotification from "@/shared/components/AppNotification.vue";
import ReceiptManager from "@/console-pos/components/ReceiptManager.vue";
import OrderHistory from "@/console-new/components/ops/orders/OrderHistory.vue";
import {useSunmiStore} from "@/console-sun/stores/sunmi.store";

export default {
	components: {
		ReceiptManager,
		AppNotification,
		CustomersViewer,
		OrderHistory,
		RefundTransactionPopup,
		BottomPayment, AppPopup, AppTextInput, ModalHeader, AppBottomSheet, OrderSummaryItem, AppButtonIcon, AppButton, AppLoader, OrderSummaryItemFake, OrderPayments},
	setup() {
		const languageStore = useLanguageStore();
		const ordersStore = useOrdersStore();
		const userStore = useUserStore();
		const printersStore = usePrintersStore();
		const notificationStore = useNotificationStore();
		const epsonStore = useEpsonStore();
		const ordersActionsStore = useOrdersActionsStore();
		const svgStore = useSvgStore();
		const counterStore = useCounterStore();
		const nativeStore = useNativeStore();
		const funnelStore = useFunnelStore();
		const currencyStore = useCurrencyStore();
		const administrationStore = useAdministrationStore();
		const sunmiStore = useSunmiStore();
		return {
			administrationStore,
			sunmiStore,
			getString: languageStore.getString,
			getIcon: svgStore.get,
			ordersStore,
			languageStore,
			currencyStore,
			funnelStore,
			userStore,
			printersStore,
			notificationStore,
			epsonStore,
			ordersActionsStore,
			counterStore,
			nativeStore,
		}
	},
	mixins: [WindowWidth],
	name: "OrderSummary",
	data() {
		return {
			activeMessageSheet: false,
			activeReprintSheet: false,
			loadingAction: false,
			loadingActionRefuse: false,
			loadingActionSms: false,
			loadingActionAde: false,
			loadingActionPrinter: false,
			loadingMessage: false,
			multiPaymentsActive: false,
			showReceiptManager: false,
			refundingTransaction: null,
			loadingRefundTransaction: false,
			loadingRefundPaymentId: null,
			showOrderHistory: null,
			openUserDetails: null,
			orderDate: null,
			orderDateInterval: null,
			loadingActionPrinterPrebill: false,
			selectedCourse: null,
			isSwipingLeft: false,
			translateX: 0,
			endingAnimation: false,
			isRefuseAction: false,
		}
	},
	computed: {
		showPreparedIcon() {
			return !this.orderIsCompleted && !this.showRefuseButton && this.userStore.currPlans.kitchen_monitor && this.userStore.currBusiness?.counter_single_item_prepared && this.ordersStore.selectedOrderGroup.status === this.ordersStore.STATUS.preparing
		},
		maxCourse() {
			if(this.ordersStore.selectedOrderGroup) {
				let maxCourse = 1;
				this.ordersStore.selectedOrderGroup.orders.forEach(order => {
					if(order.maxCourse > maxCourse) {
						maxCourse = order.maxCourse;
					}
				});
				return maxCourse;
			} else {
				return 1;
			}
		},
		runningCourse() {
			if(this.ordersStore.selectedOrderGroup) {
				let runningCourse = null;
				this.ordersStore.selectedOrderGroup.orders.forEach(order => {
					if(!runningCourse || order.course < runningCourse) {
						runningCourse = order.course;
					}
				});
				return runningCourse ? runningCourse : 1;
			} else {
				return 1;
			}
		},
		showCourses() {
			let noCoursesModes = ["takeaway", "delivery"];
			return this.counterStore.withCourses && this.ordersStore.selectedOrderGroup && !noCoursesModes.includes(this.ordersStore.selectedOrderGroup.order_mode) && !this.divideOrders && this.maxCourse > 1;
		},
		ordersCustomer() {
			if(this.ordersStore.selectedOrderGroup && this.ordersStore.selectedOrderGroup?.type !== 'multi' && this.ordersStore.selectedOrderGroup.orders.length === 1) {
				if(this.ordersStore.selectedOrderGroup.orders[0].customer_person_id) {
					return {
						customer_id: this.ordersStore.selectedOrderGroup.orders[0].customer_person_id,
						type: "person",
					};
				} else if(this.ordersStore.selectedOrderGroup.orders[0].customer_company_id) {
					return {
						customer_id: this.ordersStore.selectedOrderGroup.orders[0].customer_company_id,
						type: "company",
					};
				} else {
					return null;
				}
			} else {
				return null;
			}
		},
		ordersMultiHasPayments() {
			return this.ordersStore.selectedOrderGroup && this.ordersStore.selectedOrderGroup.type === 'multi' && this.ordersStore.selectedOrderGroup.orders.some(order => order.payments.length > 0);
		},
		multiPayments() {
			if(this.ordersMultiHasPayments) {
				let payments = [];
				this.ordersStore.selectedOrderGroup.orders.forEach(order => {
					payments = [...payments, ...order.payments];
				});
				return payments;
			} else {
				return [];
			}
		},
		orderIsCompleted() {
			return this.ordersStore.selectedOrderGroup && (this.ordersStore.selectedOrderGroup.status === this.ordersStore.STATUS.completed || this.ordersStore.selectedOrderGroup.status === this.ordersStore.STATUS.canceled || this.ordersStore.selectedOrderGroup.status === this.ordersStore.STATUS.failed);
		},
		pluginIcon() {
			if(this.ordersStore.selectedOrderGroup.paid && this.ordersStore.selectedOrderGroup.type === "external" && this.ordersStore.selectedOrderGroup.orders.length > 0 && this.ordersStore.selectedOrderGroup.orders.every(order => !!order.plugin)) {
				let plugin = this.ordersStore.selectedOrderGroup.orders[0].plugin;
				if(plugin.icon) {
					return plugin.icon;
				} else {
					return null;
				}
			}
			return null;
		},
		orderStatusIcon() {
			if(this.ordersStore.selectedOrderGroup.paid) {
				if(this.ordersStore.selectedOrderGroup.type === "klarna") {
					return "https://img.qromo.io/img/icons/klarna-logo-green.png";
				} else if(this.ordersStore.selectedOrderGroup.type === "btc") {
					return "https://img.qromo.io/img/icons/bitcoin-logo-green.png";
				} else if(this.ordersStore.selectedOrderGroup.type === "satispay") {
					return "https://img.qromo.io/img/icons/satispay-logo-green.jpg";
				} else if(this.ordersStore.selectedOrderGroup.type === "manual") {
					return "https://img.qromo.io/img/icons/icon-manual-green.svg";
				} else if(this.ordersStore.selectedOrderGroup.type === "cash") {
					return "https://img.qromo.io/img/icons/icon-cash-already-paid.svg";
				} else if(this.ordersStore.selectedOrderGroup.type === "stripe" || this.ordersStore.selectedOrderGroup.type === "numia") {
					return "https://img.qromo.io/img/icons/icon-card-payed.svg";
				} else if(this.ordersStore.selectedOrderGroup.type === "check") {
					return "https://img.qromo.io/img/icons/icon-check-already-paid.svg";
				} else if(this.ordersStore.selectedOrderGroup.type === "ticket") {
					return "https://img.qromo.io/img/icons/icon-ticket-paid.svg";
				} else if(this.ordersStore.selectedOrderGroup.type === "paylater") {
					return "https://img.qromo.io/img/icons/icon-paylater-paid-new.svg";
				} else if(this.ordersStore.selectedOrderGroup.type === "customer_credit") {
					return "https://img.qromo.io/img/icons/icon-customer-credit.svg";
				} else if(this.ordersStore.selectedOrderGroup.type === "wire") {
					return "https://img.qromo.io/img/icons/icon-wire-transfer-paid.svg";
				} else if(this.ordersStore.selectedOrderGroup.type === "multi") {
					return "https://img.qromo.io/img/icons/icon-multipay-green-new.svg";
				} else if(this.ordersStore.selectedOrderGroup.type === "external") {
					if(this.ordersStore.selectedOrderGroup.orders.length > 0 && this.ordersStore.selectedOrderGroup.orders.every(order => !!order.plugin)) {
						let plugin = this.ordersStore.selectedOrderGroup.orders[0].plugin;
						if(plugin.icon) {
							return plugin.icon;
						} else {
							return "https://img.qromo.io/img/icons/plugin-icon-payment.svg";
						}
					} else {
						return "https://img.qromo.io/img/icons/icon-paid-new.svg";
					}
				} else {
					return "https://img.qromo.io/img/icons/icon-paid-new.svg";
				}
			} else {
				if(this.ordersStore.selectedOrderGroup.type === "multi") {
					return "https://img.qromo.io/img/icons/icon-multipay-yellow-new.svg";
				} else {
					return "https://img.qromo.io/img/icons/icon-not-already-paid-new.svg";
				}
			}
		},
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else if(this.windowWidth < 1080) {
				return "720px";
			} else if(this.windowWidth < 1440) {
				return "960px";
			} else {
				return "1280px";
			}
		},
		bottomSheetWidthSmall() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else {
				return "720px";
			}
		},
		bottomSheetWidthLarge() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else if(this.windowWidth < 1080) {
				return "720px";
			} else if(this.windowWidth < 1440) {
				return "960px";
			} else {
				return "1280px";
			}
		},
		divideOrders() {
			if(this.ordersStore.selectedOrderGroup) {
				return this.ordersStore.selectedOrderGroup.orders.length !== 1;
			} else {
				return false;
			}
		},
		orderStatus() {
			if(this.ordersStore.selectedOrderGroup) {
				return this.getString(this.ordersStore.selectedOrderGroup.status);
			} else {
				return "";
			}
		},
		ordersNumber() {
			if(this.ordersStore.selectedOrderGroup) {
				if(this.ordersStore.selectedOrderGroup.orders.length > 1) {
					return this.getString("orders_num",[this.ordersStore.selectedOrderGroup.orders.length]);
				} else {
					return this.getString("order_num", [this.ordersStore.selectedOrderGroup.orders[0].orderNumberDayFormatted]);
				}
			} else {
				return "";
			}
		},
		ordersTotals() {
			let subtotal = 0;
			let additional = 0;
			let total = 0;
			let totalWithoutDiscount = 0;
			if(this.ordersStore.selectedOrderGroup) {
				this.ordersStore.selectedOrderGroup.orders.forEach(order => {
					subtotal += order.total;
					additional += order.additionalCosts;
					total += order.grandTotal;
					totalWithoutDiscount += order.grandTotal-order.subtotal_delta;
				});
			}
			return {
				subtotal: this.currencyStore.readableCurrencyValue(subtotal),
				additional: this.currencyStore.readableCurrencyValue(additional),
				total: this.currencyStore.readableCurrencyValue(total),
				totalWithoutDiscount: this.currencyStore.readableCurrencyValue(totalWithoutDiscount),
				rawTotal: total,
				rawSubtotal: subtotal,
				rawTotalWithoutDiscount: totalWithoutDiscount,
			}
		},
		ordersAction() {
			return this.ordersActionsStore.ordersGroupAction(this.ordersStore.selectedOrderGroup);
		},
		allOrdersItems() {
			let itemsObj = {};
			if(this.ordersStore.selectedOrderGroup) {
				this.ordersStore.selectedOrderGroup.orders.forEach(order => {

					if(order.tip > 0) {
						itemsObj['tip'] = {
							identifier: 'tip',
							quantity: 1,
							price: order.tip,
							menu_item_id: 'tip',
							menu_item: {
								name: this.getString('pos_tips'),
								picture: "https://img.qromo.io/img/icons/icon-fake-product.svg"
							},
							products: [],
							note: '',
							course: '',
							redeemed: 0
						};
					}

					order.items.forEach(item => {
						if(!item.menu_item) {
							return;
						}
						if(this.selectedCourse !== null && item.course !== this.selectedCourse) {
							return;
						}
						let id = item.order_item_id;
						itemsObj[id] = {
							identifier: id,
							quantity: item.quantity,
							price: item.price,
							menu_item_id: id,
							menu_item: {
								name: item.menu_item.name,
								picture: item.menu_item.picture,
							},
							products: item.products,
							note: item.note,
							course: item.course,
							redeemed: item.redeemed,
							prepared: item.prepared,
						}
					});
				});
			}
			return Object.values(itemsObj);
		},
		allOrders() {
			if(this.ordersStore.selectedOrderGroup) {
				return this.ordersStore.selectedOrderGroup.orders;
			} else {
				return [];
			}
		},
		showSmsButton() {
			let orderGroup = this.ordersStore.selectedOrderGroup;
			if(!orderGroup || !orderGroup?.orders?.length) {
				return false;
			}
			let lessThan12HoursOld = orderGroup.orders[0]['date_ordering'] && ((new Date()).getTime() - orderGroup.orders[0]['date_ordering'].getTime()) < 12*60*60*1000;
			let orderModeCheck = orderGroup.order_mode === "qr" || orderGroup.order_mode === 'takeaway';
			if(orderModeCheck && orderGroup.order_mode === 'qr') {
				orderModeCheck = !!orderGroup?.room?.enabled_sms;
			} else if(orderModeCheck && orderGroup.order_mode === 'takeaway') {
				orderModeCheck = !!this.userStore.currBusiness.takeaway_enabled_sms;
			}
			let phoneCheck = !!orderGroup.orders[0].phone && orderGroup.orders[0].phone.length > 3;
			let otherPaymentsCheck = !!this.userStore.currBusiness.sub_otherpayments_id;
			return lessThan12HoursOld && !orderGroup.orders[0].sms_sent && !!orderGroup.orders[0].type_notification && otherPaymentsCheck && orderModeCheck && phoneCheck;
		},
		showDeliveryInfoButton() {
			if(!this.ordersStore.selectedOrderGroup || this.ordersStore.selectedOrderGroup.orders.length !== 1) {
				return false;
			}
			return this.ordersStore.selectedOrderGroup.orders[0].order_mode === "delivery";
		},
		showAdeButton() {
			if(this.ordersStore.selectedOrderGroup) {
				let epsonCase = this.epsonStore.hasFiscalPrinter && this.orderIsCompleted;
				let apiscontrinoCase = this.userStore.currBusiness && this.userStore.currBusiness.apiscontrino_id;
				let receiptToEmit = false;
				let noInvoice = true;
				let notPaid = false;
				let allManual = true;
				this.ordersStore.selectedOrderGroup.orders.forEach(o => {
					if(o.type !== "manual") {
						allManual = false;
					}
					if(!o.paid) {
						notPaid = true;
						return;
					}
					if(["not_fiscalized", "pending"].includes(orderFiscalizedStatus(o))) {
						receiptToEmit = true;
					}
					if(o.invoice_id || o.customer_company_id) {
						noInvoice = false;
					}
					if(noInvoice && o?.payments?.length) {
						o.payments.forEach(pmnt => {
							if(pmnt.invoice_id || pmnt.customer_company_id) {
								noInvoice = false;
							}
						})
					}
				});
				if(allManual) {
					return false;
				}
				if(notPaid) {
					return false;
				}
				let invalidStatuses = [this.ordersStore.STATUS.unconfirmed, this.ordersStore.STATUS.canceled, this.ordersStore.STATUS.failed];
				return this.ordersStore.selectedOrderGroup.orders.length > 0 && !invalidStatuses.includes(this.ordersStore.selectedOrderGroup.status) && receiptToEmit && noInvoice && (epsonCase || apiscontrinoCase);

			} else {
				return false;
			}
		},
		showAdeSendButton() {
			if(!this.userStore.currBusiness || !this.userStore.currBusiness.apiscontrino_id) {
				return false;
			}
			let receiptId = null;
			if(this.ordersStore.selectedOrderGroup && this.ordersStore.selectedOrderGroup.orders.length > 0) {
				for(let i = 0; i < this.ordersStore.selectedOrderGroup.orders.length; i++) {
					let order = this.ordersStore.selectedOrderGroup.orders[i];
					if(!order.fiscal_request || !order.fiscal_receipt || !order.receipt_id) {
						return false;
					}
					if(!receiptId) {
						receiptId = order.receipt_id;
					} else if(receiptId !== order.receipt_id) {
						return false;
					}
				}
				return true;
			} else {
				return false;
			}
		},
		showAdeEpsonSendButton() {
			if(!isNative()) {
				return false;
			}
			let receiptId = null;
			if(this.ordersStore.selectedOrderGroup && this.ordersStore.selectedOrderGroup.orders.length > 0) {
				for(let i = 0; i < this.ordersStore.selectedOrderGroup.orders.length; i++) {
					let order = this.ordersStore.selectedOrderGroup.orders[i];
					if(!order.fiscal_request || !order.fiscal_receipt || !order.epson_receipt_id) {
						return false;
					}
					if(!receiptId) {
						receiptId = order.epson_receipt_id;
					} else if(receiptId !== order.epson_receipt_id) {
						return false;
					}
				}
				return true;
			} else {
				return false;
			}
		},
		showPrintersButton() {
			return this.ordersStore.selectedOrderGroup && (
				(!!this.printersStore.businessPrinters && this.printersStore.businessPrinters.length > 0) ||
				isNative()
			);
		},
		// the refund button relies only on data already loaded with the order: the
		// transaction is fetched on click, and it has the final say on refundability
		refundableOrder() {
			let group = this.ordersStore.selectedOrderGroup;
			if(!group || group.type !== 'stripe' || group.orders.length !== 1) {
				return null;
			}
			let order = group.orders[0];
			return order.paid && isInRefundWindow(order.date) ? order : null;
		},
		showRefundButton() {
			return this.userStore.currPermissions.includes('admin') && !!this.refundableOrder;
		},
		showEditButton() {
			let editableStatuses = [this.ordersStore.STATUS.confirmed, this.ordersStore.STATUS.accepted, this.ordersStore.STATUS.preparing];
			return this.ordersStore.selectedOrderGroup && this.ordersStore.selectedOrderGroup.orders.length === 1 && editableStatuses.includes(this.ordersStore.selectedOrderGroup.status) && this.userStore.currPlans.counter && !this.ordersStore.selectedOrderGroup.orders[0].paid;
		},
		showEmailButton() {
			return this.ordersStore.selectedOrderGroup && !this.orderIsCompleted && this.ordersStore.selectedOrderGroup.orders.length > 0 && !!this.ordersStore.selectedOrderGroup.orders[0].notice_email && this.ordersStore.selectedOrderGroup.orders[0].notice_email.length > 3;
		},
		showRefuseButton() {
			return this.ordersStore.selectedOrderGroup && this.ordersStore.selectedOrderGroup.status === this.ordersStore.STATUS.confirmed && ["delivery", "takeaway"].includes(this.ordersStore.selectedOrderGroup.order_mode);
		},
		showPaymentSecondaryButton() {
			const orderGroup = this.ordersStore.selectedOrderGroup;
			const business = this.userStore.currBusiness;
			if(!orderGroup || !business || this.orderIsCompleted || this.showRefuseButton) {
				return false;
			}

			const hasOrders = orderGroup.orders.length > 0;
			const hasUnpaidOrders = orderGroup.orders.some(o => !o.paid);
			const businessUsesCounterCourses = !!business.counter_courses;

			const payableStatuses = [this.ordersStore.STATUS.accepted, this.ordersStore.STATUS.preparing, this.ordersStore.STATUS.prepared];
			const isInPayableStatus = payableStatuses.includes(orderGroup.status);

			const isNextAction = this.ordersAction.actionId === "next";
			const showPreparedButton = this.userStore.currPlans.kitchen_monitor && orderGroup.status === this.ordersStore.STATUS.preparing && business.counter_allow_prepared;
			const isRelevantAction = isNextAction || showPreparedButton;

			return hasOrders && hasUnpaidOrders && businessUsesCounterCourses && isInPayableStatus && isRelevantAction;
		},
		showCancelButton() {
			let allUnpaid = true;
			let isPartial = false;
			let allUnfiscalized = true;
			if(this.ordersStore.selectedOrderGroup && this.ordersStore.selectedOrderGroup.orders.length > 0 && !this.orderIsCompleted && [this.ordersStore.STATUS.confirmed, this.ordersStore.STATUS.accepted, this.ordersStore.STATUS.preparing, this.ordersStore.STATUS.prepared].includes(this.ordersStore.selectedOrderGroup.status) && !this.showRefuseButton) {
				this.ordersStore.selectedOrderGroup.orders.forEach(o => {
					if(o.paid) {
						allUnpaid = false;
					} else {
						if(o.type === "multi" && o.payments.length > 0) {
							isPartial = true;
						}
					}
					if(orderFiscalizedStatus(o) !== "not_fiscalized") {
						allUnfiscalized = false;
					}
				});
				return allUnpaid && allUnfiscalized && !isPartial;
			} else {
				return false;
			}
		},
		showCancelPartialButton() {
			let allPaid = true;
			let isPartial = false;
			if(this.ordersStore.selectedOrderGroup && this.ordersStore.selectedOrderGroup.orders.length > 0 && !this.orderIsCompleted && [this.ordersStore.STATUS.confirmed, this.ordersStore.STATUS.accepted, this.ordersStore.STATUS.preparing, this.ordersStore.STATUS.prepared].includes(this.ordersStore.selectedOrderGroup.status) && !this.showRefuseButton && !this.showCancelButton) {
				this.ordersStore.selectedOrderGroup.orders.forEach(o => {
					if(!o.paid) {
						allPaid = false;
						if(o.type === "multi" && o.payments.length > 0) {
							isPartial = true;
						}
					}
				});
				return isPartial && !allPaid;
			} else {
				return false;
			}
		},
		noMiniActions() {
			return !this.showRefundButton &&
				!this.showEmailButton &&
				!this.showEditButton &&
				!this.showPrintersButton &&
				!this.showSmsButton &&
				!this.showAdeButton &&
				!this.showAdeSendButton &&
				!this.showAdeEpsonSendButton &&
				!this.showDeliveryInfoButton &&
				!this.showCancelPartialButton &&
				!this.showCancelButton;
		},
		orderGroupIds() {
			let orderIds = [];
			if(this.ordersStore.selectedOrderGroup) {
				this.ordersStore.selectedOrderGroup.orders.forEach(order => {
					orderIds.push(order.order_id);
				});
			}
			return orderIds;
		},
		canShareLink() {
			return this.ordersStore.selectedOrderGroup && this.ordersStore.selectedOrderGroup.orders.length === 1 && this.ordersStore.selectedOrderGroup.orders[0].id_encrypted && this.userStore.currBusiness && this.userStore.currBusiness.link;
		},
		disableMainAction() {
			let disabled = true;

			if(this.ordersStore.selectedOrderGroup) {
				if(!this.orderIsCompleted) {
					disabled = (this.ordersAction.actionId === "payment" && !this.userStore.checkAnyOrderPayAllowed) ||
						(this.ordersAction.actionId === "next" && (!this.userStore.checkOrderEditAllowed && !this.userStore.checkOrderCreationAllowed)) ||
						(this.ordersAction.actionId === 'complete' && !this.userStore.checkAnyOrderPayAllowed && !this.userStore.checkOrderCreationAllowed);
				}
				else {
					disabled = this.orderIsCompleted;
				}
			}

			return disabled;
		}
	},
	methods: {
		clickOrderHead() {
			if(this.ordersStore.selectedOrderGroup?.orders?.length === 1) {
				this.showOrderHistory = this.ordersStore.selectedOrderGroup.orders[0];
			}
		},
		clickSubOrderHead(index) {
			if(this.ordersStore.selectedOrderGroup?.orders?.[index]) {
				this.showOrderHistory = this.ordersStore.selectedOrderGroup.orders[index];
			}
		},
		// the transaction is fetched only when the user actually asks for a refund: nothing
		// about refundability is kept on the order, so nothing can go stale after a refund
		fetchTransactionAndRefund(orderId, pickTransaction, done) {
			API.init().getOrderTransactions(this.userStore.currBusinessId, orderId, response => {
				done();
				if(response?.result !== "OK") {
					this.notificationStore.showNotification('generic-error');
					return;
				}
				let transaction = pickTransaction(response);
				transaction = transaction ? this.administrationStore.normalizeTransaction(transaction) : null;
				if(isTransactionRefundable(transaction)) {
					this.refundingTransaction = transaction;
				} else if(transaction?.refunded_amount) {
					// a refund already happened: say so instead of the generic "no longer refundable"
					this.notificationStore.showNotification(transaction.refunded_amount < transaction.amount ? 'refund-already-partially-refunded-error' : 'refund-already-refunded-error');
				} else {
					this.notificationStore.showNotification('refund-not-available-error');
				}
			});
		},
		startOrderRefund() {
			let order = this.refundableOrder;
			if(this.loadingRefundTransaction || !order) { return; }

			this.loadingRefundTransaction = true;
			this.fetchTransactionAndRefund(
				order.order_id,
				response => response.transaction,
				() => { this.loadingRefundTransaction = false; }
			);
		},
		startPaymentRefund(payment) {
			if(this.loadingRefundPaymentId !== null || !payment?.order_id) { return; }

			this.loadingRefundPaymentId = payment.order_payment_id;
			this.fetchTransactionAndRefund(
				payment.order_id,
				response => (response.payments || []).find(p => p.order_payment_id === payment.order_payment_id)?.transaction,
				() => { this.loadingRefundPaymentId = null; }
			);
		},
		refundSuccess() {
			this.notificationStore.showNotification('refunded-transaction-success');
			this.ordersStore.refreshOrders();
		},
		refundFailed(details) {
			this.notificationStore.showNotification(details === 'refund_limit_exceeded' ? 'refund-limit-exceeded-error' : 'generic-error');
		},
		successReceipt() {
			this.notificationStore.showNotification("generic-success");
		},
		openPaymentUserDetails(userToShow) {
			this.openUserDetails = userToShow;
		},
		coverChargeName(order) {
			if(order?.room && order.room?.cover_charge_name) {
				return order.room?.cover_charge_name || this.getString('cover_charge');
			}
			return this.getString('cover_charge');
		},
		swipeHandler(trigger, isRight, translationX) {
			if(!this.endingAnimation && this.windowWidth <= 750) {
				if(!isRight) {
					this.translateX = translationX;
					this.isSwipingLeft = isRight;
				}
			}
		},
		clickCourse(course) {
			if(this.selectedCourse === course) {
				this.selectedCourse = null;
			} else {
				this.selectedCourse = course;
			}
		},
		isSingleOrderEditable(order) {
			let editableStatuses = [this.ordersStore.STATUS.confirmed, this.ordersStore.STATUS.accepted, this.ordersStore.STATUS.preparing];
			return order && order.type !== 'multi' && editableStatuses.includes(order.status) && this.userStore.currPlans.counter;
		},
		shareLink() {
			let link = (LOCAL ? "http://" : "https://") + this.userStore.currBusiness.link + "." + HOST + "/order/?id=" + this.ordersStore.selectedOrderGroup.orders[0].id_encrypted;
			if(navigator && navigator.share) {
				navigator.share({
					title: this.ordersStore.selectedOrderGroup.title,
					text: this.ordersStore.selectedOrderGroup.orders[0].orderNumberDayFormatted,
					url: link,
				}).then(() => {
					console.log("Shared successfully");
				}).catch(() => {
					console.log("Error in sharing");
				});
			} else if(navigator && navigator.clipboard) {
				navigator.clipboard.writeText(link).then(() => {
					this.notificationStore.showNotification("link-copied");
				}).catch(() => {
					this.notificationStore.showNotification("generic-fail");
				});
			} else if(TEST) {
				console.log('Order ' + this.ordersStore.selectedOrderGroup.orders[0].orderNumberDayFormatted + ' link: ', link)
				this.notificationStore.showNotification("link-in-console");
			}
		},
		showDeliveryInfo() {
			if(!this.ordersStore.selectedOrderGroup || this.ordersStore.selectedOrderGroup.orders.length !== 1) {
				return false;
			}
			let order = this.ordersStore.selectedOrderGroup.orders[0];
			this.ordersStore.deliveryOrderInfo = {
				customer_name: order.user_name,
				customer_phone: order.user_phone,
				address: order.title,
				address_details: order.address ? order.address.details : null,
				delivery_date: order.delivery_scheduled_date ? order.subtitle : null,
				tracking_url: order.rate ? order.rate.tracking_url : null,
				label_url: order.rate ? order.rate.label_url : null,
				rate: order.rate,
				orderNumberDayFormatted: order.orderNumberDayFormatted
			};
		},
		itemIsDone(item) {
			if(item.identifier === 'tip') {
				return false;
			}
			if(this.orderIsCompleted) {
				return false;
			}
			if(this.ordersStore.selectedOrderGroup.status === this.ordersStore.STATUS.prepared) {
				return true;
			}
			return !!this.ordersStore?.selectedOrderGroup?.orders?.length && this.ordersStore.selectedOrderGroup.orders[0].course > item.course;
		},
		togglingMultiPayments(isOpen) {
			if(!isOpen) {
				this.multiPaymentsActive = false;
			}
		},
		toggleBottomSheetReprint(isOpen) {
			if(!isOpen) {
				this.activeReprintSheet = false;
			}
		},
		toggleBottomSheetUserDetails(isOpen) {
			if(!isOpen) {
				this.openUserDetails = null;
			}
		},
		toggleReceiptManager(isOpen) {
			if(!isOpen) {
				this.showReceiptManager = false;
			}
		},
		toggleOrderHistory(isOpen) {
			if(!isOpen) {
				this.showOrderHistory = null;
			}
		},
		ordersActionClicked(forceAction = null) {
			if(this.loadingAction) return;
			this.loadingAction = true;
			const actionId = forceAction ? forceAction : this.ordersAction.actionId;
			const groupId = this.ordersStore.selectedOrderGroup.groupIdentifier;
			this.ordersActionsStore.performAction(actionId, this.ordersStore.selectedOrderGroup, (success, details) => {
				if(!success) {
					if(details) {
						if(details === "max_course_reached") {
							this.notificationStore.showNotification("last-course-reached")
						} else {
							this.notificationStore.showNotification("generic-fail")
						}
					} else {
						this.notificationStore.showNotification("generic-fail")
					}
				}
				this.loadingAction = false;

				// Smart next-course selection
				if(actionId === "next" && groupId === this.ordersStore.selectedOrderGroup.groupIdentifier && !this.divideOrders && this.selectedCourse !== null) {
					this.selectedCourse = this.runningCourse;
				}
			});
		},
		singleOrderHasAction(order) {
			if(this.ordersAction.actionId === "prepare_done" && order.status === this.ordersStore.STATUS.prepared) {
				return false;
			}
			return true;
		},
		singleOrderActionText(order) {
			let noCoursesModes = ["takeaway", "delivery"];
			if(
				this.userStore.currBusiness.counter_courses &&
				!noCoursesModes.includes(this.ordersStore.selectedOrderGroup.order_mode) &&
				this.ordersAction.actionId === "next"
			) {
				let orderLastCourse = order.items.reduce((max, item) => Math.max(max, item.course), 0);
				let isLastCourse = order.course >= orderLastCourse;
				if(isLastCourse) {
					let advancedPayments = !order.paid && this.userStore.currPlans.counter;
					return advancedPayments ? this.getString("payment") : this.getString("completed");
				} else {
					return this.getString("next");
				}
			} else {
				return this.ordersAction.text;
			}
		},
		orderActionClicked(order) {
			this.loadingAction = true;

			let actionId = this.ordersAction.actionId;
			let noCoursesModes = ["takeaway", "delivery"];
			if(this.userStore.currBusiness.counter_courses && !noCoursesModes.includes(this.ordersStore.selectedOrderGroup.order_mode) && this.ordersAction.actionId === "next") {
				let orderLastCourse = order.items.reduce((max, item) => Math.max(max, item.course), 0);
				let isLastCourse = order.course >= orderLastCourse;
				if(isLastCourse) {
					let advancedPayments = !order.paid && this.userStore.currPlans.counter;
					actionId = advancedPayments ? "payment" : "complete";
				}
			}
			this.ordersActionsStore.performActionSingleOrder(actionId, order, (success) => {
				if(!success) {
					this.notificationStore.showNotification("generic-fail")
				}
				this.loadingAction = false;
			});
		},
		getOrderFormattedDate(order) {
			if(this.orderIsCompleted) {
				return formatDateMMDDHHmm(order.date);
			} else {
				return getMinutesPassedFromDate(order.date, true);
			}
		},
		sendSms() {
			if(this.loadingActionSms) return;
			this.loadingActionSms = true;
			this.ordersActionsStore.sendSms(this.orderGroupIds, (success, errorDetails) => {
				if(success) {
					if(this.ordersStore.selectedOrderGroup) {
						let updatesArray = [];
						this.ordersStore.selectedOrderGroup.orders.forEach(order => {
							updatesArray.push({
								order_id: order.order_id,
								sms_sent: true,
							});
						});
						this.ordersStore.applyOrdersUpdates(updatesArray, false);
					}
					this.notificationStore.showNotification("message-sent")
				} else {
					let notificationName = "generic-fail";
					if(errorDetails === 'missing_phone') {
						notificationName = "sms-missing-phone";
					} else if(errorDetails === 'missing_type_notification') {
						notificationName = "sms-missing-type";
					} else if(errorDetails === 'failed_charge') {
						notificationName = "sms-failed-charge";
					}
					this.notificationStore.showNotification(notificationName)
				}
				this.loadingActionSms = false;
			});
		},
		printOrderAgain() {
			if(this.loadingActionPrinter) return;
			if((!!this.printersStore.businessPrinters && this.printersStore.businessPrinters.length > 0) || this.sunmiStore.canLocalPrint) {
				this.activeReprintSheet = true;
			} else {
				this.printAggregateOrder();
			}
		},
		printAggregateOrder() {
			this.loadingActionPrinter = true;
			this.ordersActionsStore.printAggregateOrder(this.ordersStore.selectedOrderGroup, (success) => {
				if(success) {
					this.notificationStore.showNotification("print-success");
				}
				this.loadingActionPrinter = false;
			});
		},
		sendPrintPrebill(allItems) {

			this.loadingActionPrinterPrebill = true;
			this.$refs["prebill-type"].toggleStatus(false);
			this.ordersActionsStore.printPrebill(this.ordersStore.selectedOrderGroup, allItems, (success) => {
				if(success) {
					this.notificationStore.showNotification("print-success");
				}
				this.loadingActionPrinterPrebill = false;
			});

		},
		printPrebill() {

			// check if this.ordersStore.selectedOrderGroup contains paid items, in which case we show the popup
			let hasPaidItems = false;
			for (let i = 0; i < this.ordersStore.selectedOrderGroup.orders.length; i++) {
				let order = this.ordersStore.selectedOrderGroup.orders[i];
				if(order.paid) {
					continue;
				}
				for (let j = 0; j < order.items.length; j++) {
					let item = order.items[j];
					if (item.redeemed > 0) {
						hasPaidItems = true;
						break;
					}
				}
				if (hasPaidItems) {
					break;
				}
			}

			if(hasPaidItems) {
				this.$refs["prebill-type"].toggleStatus(true);
			} else {
				this.sendPrintPrebill(true);
			}

		},
		printFiscalReceipt() {
			if(this.loadingActionAde) return;
			this.loadingActionAde = true;
			if(this.userStore.currBusiness && this.userStore.currBusiness.apiscontrino_id) {
				let orderIds = this.ordersStore.selectedOrderGroup.orders.map(order => {
					return order.order_id;
				});
				this.ordersActionsStore.setExpectingFiscalPrint();
				this.epsonStore.generateFiscalReceiptApiscontrino(orderIds, (receiptId) => {
					if(receiptId) {
						this.notificationStore.showNotification("print-success");
						this.ordersStore.refreshOrders();
					} else {
						this.notificationStore.showNotification("generic-fail");
					}
					this.loadingActionAde = false;
				});
			} else {
				this.epsonStore.printFiscalReceipt(this.ordersStore.selectedOrderGroup.orders, true);
			}
		},
		showSendReceipt() {
			if(this.ordersStore.selectedOrderGroup.orders.length > 0) {
				this.ordersStore.sendReceiptData = this.ordersStore.selectedOrderGroup.orders[0];
			}
		},
		showSendReceiptEpson() {
			if(this.ordersStore.selectedOrderGroup.orders.length > 0) {
				this.ordersStore.sendReceiptEpsonData = this.ordersStore.selectedOrderGroup.orders[0];
			}
		},
		editOrders() {
			if(this.ordersStore.selectedOrderGroup && this.ordersStore.selectedOrderGroup.orders.length > 0) {
				this.editOrder(this.ordersStore.selectedOrderGroup.orders[0]);
			}
		},
		editOrder(order) {
			this.counterStore.startEditOrder(order);
			this.$router.push("/ops/counter");
		},
		refuseOrder() {
			if(this.loadingActionRefuse) return;
			if(this.ordersStore.selectedOrderGroup && this.ordersStore.selectedOrderGroup.orders.length > 0) {
				this.$refs["refuse-confirm"].toggleStatus(false);
				this.loadingActionRefuse = true;
				const action = this.isRefuseAction ? "refuse" : "cancel";
				this.ordersActionsStore.changeOrders([this.ordersStore.selectedOrderGroup.orders[0].order_id], action, (success) => {
					if(success) {
						if(this.ordersStore.selectedOrderGroup) {
							let updatesArray = [];
							this.ordersStore.selectedOrderGroup.orders.forEach(order => {
								updatesArray.push({
									order_id: order.order_id,
									status: "canceled"
								});
							});
							this.ordersStore.applyOrdersUpdates(updatesArray, false);
						}
						if(this.counterStore.editOrder) {
							this.counterStore.finishEditOrder();
						}
					} else {
						this.notificationStore.showNotification("generic-fail")
					}
					this.loadingActionRefuse = false;
				});
			}
		},
		refusePartialOrder() {
			if(this.loadingActionRefuse) return;
			if(this.ordersStore.selectedOrderGroup && this.ordersStore.selectedOrderGroup.orders.length > 0) {
				this.$refs["refuse-partial-confirm"].toggleStatus(false);
				this.loadingActionRefuse = true;
				this.ordersActionsStore.changeOrders([this.ordersStore.selectedOrderGroup.orders[0].order_id], "cancel_partial", (success) => {
					if(success) {
						if(this.ordersStore.selectedOrderGroup) {
							this.ordersStore.resetOrders();
							this.ordersStore.refreshOrders();
						}
						if(this.counterStore.editOrder) {
							this.counterStore.finishEditOrder();
						}
					} else {
						this.notificationStore.showNotification("generic-fail")
					}
					this.loadingActionRefuse = false;
				});
			}
		},
		performUniteOrder() {

			if(this.ordersStore.selectedOrderGroup && this.ordersStore.selectedOrderGroup.orders.length > 0) {
				this.loadingAction = true;
				let orderIds = this.ordersStore.selectedOrderGroup.orders.map(order => {
					return order.order_id;
				});
				this.ordersActionsStore.uniteOrders(orderIds, (success) => {
					if(success) {
						if(this.ordersStore.selectedOrderGroup) {
							this.ordersStore.resetOrders();
							this.ordersStore.refreshOrders();
						}
						if(this.counterStore.editOrder) {
							this.counterStore.finishEditOrder();
						}
					} else {
						this.notificationStore.showNotification("generic-fail")
					}
					this.loadingAction = false;
				});
			}

		},
		toggleBottomSheet(isOpen) {
			if(!isOpen) {
				this.activeMessageSheet = false;
			}
		},
		sendOrderMessage() {
			let text = this.$refs.messageInput.currentValue;
			if(text && text.length > 0) {
				this.loadingMessage = true;
				this.ordersActionsStore.sendOrderMessage(this.orderGroupIds, text, (success) => {
					this.activeMessageSheet = false;
					this.$refs.messageInput.currentValue = "";
					this.loadingMessage = false;
					if(success) {
						this.notificationStore.showNotification("message-sent");
					} else {
						this.notificationStore.showNotification("generic-fail");
					}
				});
			}
		},
		refreshOrderDate() {
			if(this.ordersStore.selectedOrderGroup) {
				if(this.orderIsCompleted) {
					return formatDateMMDDHHmm(this.ordersStore.selectedOrderGroup.date);
				} else {
					return getMinutesPassedFromDate(this.ordersStore.selectedOrderGroup.date, true);
				}
			} else {
				return null;
			}
		},
		resetSwipe() {
			this.translateX = 0;
			this.endingAnimation = false;
			this.isSwipingLeft = false;
		},
		selectedGroupChanged() {
			this.orderDate = this.refreshOrderDate();

			let orderGroup = this.ordersStore.selectedOrderGroup;
			if(orderGroup) {
				this.ordersStore.addToOrdersSeen(orderGroup.orders.map(o => o.order_id));
				orderGroup.alert = false;

				if(this.ordersStore.ctaIsDueForOrderId && this.ordersStore.selectedOrderGroup && this.ordersStore.selectedOrderGroup.orders.length === 1 && Number(this.ordersStore.selectedOrderGroup.orders[0].order_id) === Number(this.ordersStore.ctaIsDueForOrderId)) {
					this.ordersStore.ctaIsDueForOrderId = null;
					if(this.ordersAction.actionId === "payment") {
						this.ordersActionClicked();
					} else if(this.showPaymentSecondaryButton) {
						this.ordersActionClicked("payment");
					} else {
						console.warn("CTA is due but no payment action available");
					}
				}

				this.selectedCourse = null;
			}

		}
	},
	mounted() {
		this.orderDateInterval = setInterval(() => {
			this.orderDate = this.refreshOrderDate();
		}, 30000);
		if(this.ordersStore.ctaIsDueForOrderId && this.ordersStore.selectedOrderGroup && this.ordersStore.selectedOrderGroup.orders.length === 1 && Number(this.ordersStore.selectedOrderGroup.orders[0].order_id) === Number(this.ordersStore.ctaIsDueForOrderId)) {
			this.ordersStore.ctaIsDueForOrderId = null;
			if(this.ordersAction.actionId === "payment") {
				this.ordersActionClicked();
			} else if(this.showPaymentSecondaryButton) {
				this.ordersActionClicked("payment");
			} else {
				console.warn("CTA is due but no payment action available");
			}
		}
		this.selectedCourse = null;
		this.selectedGroupChanged();
	},
	beforeUnmount() {
		clearInterval(this.orderDateInterval);
		this.resetSwipe();
	},
	watch: {
		"ordersStore.transitioningMobileOrderSummary"(val) {
			if(!val) {
				this.resetSwipe();
			}
		},
		"ordersStore.selectedGroupId": {
			handler: function () {
				this.selectedGroupChanged();
			}
		},
		"epsonStore.fiscalPrinterFailed": {
			handler: function (failed) {
				if(failed) {
					this.loadingActionAde = false;
				}
			},
			deep: true
		},
		"epsonStore.fiscalPrinterFailedSilent": {
			handler: function (failed) {
				if(failed) {
					this.loadingActionAde = false;
					this.epsonStore.fiscalPrinterFailedSilent = false;
				}
			},
			deep: true
		},
		showAdeButton: {
			handler: function (val, oldVal) {
				if(val !== oldVal) {
					this.loadingActionAde = false;
				}
			}
		},
		"ordersStore.ctaIsDueForOrderId": {
			handler: function (orderId) {
				if(orderId && this.ordersStore.selectedOrderGroup && this.ordersStore.selectedOrderGroup.orders.length === 1 && Number(this.ordersStore.selectedOrderGroup.orders[0].order_id) === Number(orderId)) {
					this.ordersStore.ctaIsDueForOrderId = null;
					this.ordersActionClicked();
				}
			}
		},
	}
}
</script>

<style lang="scss" scoped>
.order-summary-container {
	--summary-h-padding: 20px;
	--summary-v-padding: 10px;

	background-color: var(--color-bg);
	box-shadow: 0 0 13px var(--color-box-shadow-1);
	border-radius: 13px;
	height: 100%;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	transform: translateX(0);
	transition: transform 0.15s ease, height 0.2s linear, padding 0.2s linear, margin-right 0.5s ease;

	&.swipe-delete {
		transition: transform 0.01s ease, height 0.2s linear, padding 0.2s linear, margin-right 0.5s ease;
	}

	@media (max-width: 1080px) {
		--summary-h-padding: 16px;
	}

	@media (max-width: 420px) {
		--summary-h-padding: 12px;
	}

	.order-head {
		display: flex;
		flex-direction: column;
		padding: var(--summary-v-padding) var(--summary-h-padding);
		border-bottom: 1px solid var(--color-border-2);

		&:not(.no-pointer) {
			cursor: pointer;
		}

		.first-row {
			display: flex;
			justify-content: space-between;
			align-items: flex-start;
			margin-bottom: 8px;

			.history-button {
				display: flex;
				align-items: center;
				gap: 6px;
				padding: 6px 12px;
				border-radius: 8px;
				background: var(--color-bg-primary-2);
				cursor: pointer;
				transition: background-color 0.2s;
				width: 100%;
				justify-content: center;

				&:hover {
					background: var(--color-bg-primary-1);
				}

				img {
					width: 16px;
					height: 16px;
				}

				span {
					font-size: 14px;
					color: var(--color-txt);
					font-weight: 500;
				}
			}

			.title-container {
				display: flex;
				flex-direction: row;
				align-items: center;
				gap: 4px;
				overflow: hidden;

				.title-icon {
					width: 20px;
					height: 20px;
					display: flex;
					justify-content: center;
					align-items: center;
					padding: 3px;
					flex-shrink: 0;
					cursor: pointer;

					&:deep(svg) {
						width: 100%;
						height: 100%;

						.fill {
							fill: var(--color-primary);
						}

						.stroke {
							stroke: var(--color-primary);
						}
					}
				}

				.title {
					font-size: 18px;
					font-weight: 700;
					color: $primary;
					@include truncateString(1);
				}
			}

			.date {
				font-size: 14px;
				color: #B2B2B2;
				text-align: right;
				flex-shrink: 0;
				padding-left: 4px;
				@include truncateString(1);
			}
		}

		.second-row {
			display: flex;
			justify-content: space-between;
			align-items: center;

			.state-container {
				display: flex;
				flex-direction: row;
				align-items: center;
				justify-content: flex-start;
				margin-right: 6px;

				.payment-container {
					display: flex;
					flex-direction: row;
					align-items: center;
					margin-right: 6px;
					border-radius: 30px;

					.payment-icon {
						height: 28px;
						width: 28px;
						border-radius: 13px;
						display: flex;
						justify-content: center;
						align-items: center;

						&.pluginIcon {
							background-color: #62d18d;
							padding: 4px;
						}

						img {
							height: 100%;
							border-radius: 30px;
						}
					}
				}

				.order-state {

					.status {
						font-size: 14px;
						font-weight: 700;
						color: var(--status-color);
						@include truncateString();
					}

				}
			}

			.order-share-container {
				display: flex;
				flex-direction: row;
				align-items: center;
				justify-content: flex-end;
				gap: 8px;
				padding: 5px 0;
				cursor: pointer;

				.orders-number {
					font-size: 14px;
					color: var(--color-txt);
					text-align: right;
					@include truncateString(1);
				}

				.share-icon {
					width: 16px;
					height: 16px;
					display: flex;
					justify-content: center;
					align-items: center;

					&:deep(svg) {
						width: 100%;
						height: 100%;

						.fill {
							fill: $primary;
						}

						.stroke {
							stroke: $primary;
						}
					}
				}
			}

		}
	}

	.courses-selector-container {
		overflow-x: auto;
		white-space: nowrap;
		@include hideScrollbar();
		box-shadow: inset 0 2px 3px rgba(7, 82, 163, 0.1), inset 0 -2px 3px rgba(7, 82, 163, 0.1);
		flex-shrink: 0;
		display: flex;
		border-top: 1px solid var(--color-border-2);

		.courses-selector {
			display: flex;
			gap: 8px;
			padding: 8px var(--summary-h-padding);

			.course-selector {
				border-radius: 13px;
				border: 1px solid var(--color-primary);
				min-width: 36px;
				height: 36px;
				text-align: center;
				font-size: 16px;
				color: var(--color-primary);
				cursor: pointer;
				transition: background-color 0.25s ease-in-out, color 0.25s ease-in-out;
				display: flex;
				justify-content: center;
				align-items: center;
				user-select: none;

				&.selected {
					background-color: var(--color-active-button);
				}

				&.done {
					border-color: var(--color-primary);
					color: var(--color-primary);
				}

				&.current {
					background-color: var(--color-primary);
					color: var(--color-bg);

					&.selected {
						background-color: var(--color-secondary);
						color: var(--color-primary);
					}
				}
			}
		}
	}

	.order-items {
		flex-grow: 1;
		overflow-y: auto;
		@include hideScrollbar();

		.single-order-actions {
			width: 100%;
			display: flex;
			justify-content: space-between;
			gap: 8px;
		}

		.notes-container {
			border-top: 1px solid var(--color-border-2);

			.notes-title {
				font-size: 16px;
				color: var(--color-txt);
				font-weight: 700;
				margin-bottom: 4px;
			}

			.notes-body {
				font-size: 14px;
				color: var(--color-txt);
				white-space: pre-wrap;
				word-wrap: break-word;
			}
		}

		.all-items-together {

			.notes-container {
				padding: 8px var(--summary-h-padding);
			}

			.button-unite {
				margin: 8px var(--summary-h-padding);
				width: calc(100% - var(--summary-h-padding) - var(--summary-h-padding));
				padding: 5px 7px;
			}

			.items-summary-container {

				.order-summary-item {
					padding: var(--summary-v-padding) var(--summary-h-padding);
					border-bottom: 1px solid var(--color-border-2);
				}

				.order-summary-item:last-child {
					border-bottom: 0;
				}

			}
		}

		.items-divided {

			.notes-container {
				padding: 8px 0;
			}

			.button-divide {
				margin: 8px var(--summary-h-padding);
				width: calc(100% - var(--summary-h-padding) - var(--summary-h-padding));
				padding: 5px 7px;
			}

			.items-divided-order {
				padding: 0 var(--summary-h-padding);
				padding-top: 8px;
				padding-bottom: 8px;
				background-color: var(--color-bg-primary-2);
				margin-bottom: 8px;


				.items-summary-container {
					.order-summary-item {
						border-bottom: 1px solid var(--color-border-2);
						padding: var(--summary-v-padding) 0;
					}

					.order-summary-item:last-child {
						border-bottom: 0;
					}
				}

				.items-order-header {
					box-shadow: inset 0 1px 2px var(--color-alpha-4);
					background-color: var(--color-bg);
					padding: 4px 12px;
					display: flex;
					justify-content: space-between;
					align-items: center;
					border-radius: 13px;
					cursor: pointer;

					.items-order-number {
						font-size: 14px;
						color: var(--color-txt);
					}

					.items-order-date {
						font-size: 14px;
						color: #B2B2B2;
					}
				}

				.button-divided-action {
					width: 100%;
					padding: 5px 7px;
				}

			}
		}

		.no-items {
			padding: 0 var(--summary-h-padding);
			padding-top: 8px;
			padding-bottom: 8px;
		}
	}

	&.noMiniActions {
		.order-items {
			height: calc(100% - 234.5px);
		}

		.order-bottom {
			.order-actions {
				.row-mini-actions {
					display: none;
				}
			}

			.order-totals {
				.total-row-separator:last-child {
					background: transparent;
				}
			}
		}

	}

	@media (max-height: 550px) {
		min-height: unset;
		height: auto;
		display: block;

		.order-items {
			min-height: 245px;
			height: auto;
			overflow-y: unset;
		}
	}

	.order-bottom {
		display: flex;
		flex-direction: column;

		.order-totals {
			display: flex;
			flex-direction: column;
			align-items: flex-start;
			padding: var(--summary-v-padding) var(--summary-h-padding);
			border-top: 1px solid var(--color-border-2);

			.total-row {
				display: flex;
				justify-content: space-between;
				align-items: center;
				width: 100%;

				&-title {
					font-size: 14px;
					color: #B2B2B2;
				}

				&-value {
					font-size: 14px;
					color: var(--color-txt);
				}

				.total-bold {
					font-weight: 700;
				}

				.total-row-value img {
					cursor: pointer;
					width: 14px;
					height: 14px;
					padding-left: 4px;
					padding-right: 6px;
					box-sizing: content-box;
				}

				.total-value-discount {
					font-size: 12px;
					color: #B2B2B2;
					text-decoration: line-through;
					margin-right: 4px;
					line-height: 15px;
				}
			}

			.total-row-separator {
				width: 100%;
				height: 1px;
				background-color: var(--color-border-2);
				margin: 4px 0;
			}
		}

		.order-actions {
			display: flex;
			flex-direction: column;
			align-items: flex-start;

			.row-mini-actions {
				display: flex;
				justify-content: center;
				align-items: center;
				overflow: hidden;
				width: 100%;

				.mini-action {
					height: 44px;
					flex: 1;
				}

			}

			.row-main-action {
				width: 100%;
				display: flex;
				justify-content: center;
				align-items: center;
				border-bottom-left-radius: 13px;
				border-bottom-right-radius: 13px;
				overflow: hidden;

				.refuse {
					width: 30%;
					padding: 7px 10px;
					border-bottom-left-radius: 13px;
				}

				.accept {
					width: 70%;
					padding: 8px 10px;

					&.fullWidth {
						width: 100%;
					}
				}
			}
		}
	}

	.ghost-placeholder {
		width: 60px;
		height: 18px;
		background-color: $alpha_1;
		border-radius: 4px;

		&.long {
			width: 110px;
		}

		&.short {
			width: 40px;
		}

		&.full {
			width: 100%;
		}

	}

	.ghost-button-placeholder {
		width: 100%;
		height: 32px;
		background-color: #25539D3B;
		border-radius: 13px;
	}

	// Custom button styles to match CounterOrder
	.custom-icon-button {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		background-color: var(--color-bg);
		cursor: pointer;
		border-top: 1px solid var(--color-primary);
		border-right: 1px solid var(--color-primary);

		&:last-child {
			border-right: none;
		}
		
		.btn-inner {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 100%;
			height: 100%;
			background-color: var(--color-bg);

			img {
				width: 16px;
				height: 16px;
			}
		}
		
		&.disabled {
			cursor: not-allowed;
			pointer-events: none;
			opacity: 0.5;
		}
		
		&.loading {
			.btn-inner img {
				visibility: hidden;
			}
		}
	}
	
	.custom-button {
		position: relative;
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
		background-color: var(--color-primary);
		height: 50px;
		padding: 0 22px;

		.button-text {
			font-weight: 500;
			font-size: 16px;
			color: var(--color-txt-white);
		}
		
		&.outline {
			background-color: transparent;
			border: 1px solid var(--color-primary);

			.button-text {
				color: var(--color-primary);
			}
		}
		
		&.disabled {
			cursor: not-allowed;
			pointer-events: none;
			opacity: 0.5;
		}
		
		&.loading {
			.button-text {
				visibility: hidden;
			}
		}

		.app-loader {
			position: absolute;
		}

	}

}
</style>
